import React from "react";
import { I18n } from "i18n-js";

import fi from "@/translations/fi.json";
import en from "@/translations/en.json";
import es from "@/translations/es.json";

const translations = {
  fi: fi,
  en: en,
  es: es,
};

export type SupportedLanguages = "fi" | "en" | "es";

const supportedLanguages = [
  { langCode: "fi", countryCode: "FI", name: "Suomi" },
  { langCode: "en", countryCode: "US", name: "English" },
  { langCode: "es", countryCode: "ES", name: "Español" },
];

// get user language from browser
const userLang = navigator.language.split("-")[0] as SupportedLanguages;
const savedLang = localStorage.getItem(
  "preferred-language",
) as SupportedLanguages | null;
const resolvedLang =
  savedLang && supportedLanguages.find((l) => l.langCode === savedLang)
    ? savedLang
    : (supportedLanguages.find((l) => l.langCode === userLang)?.langCode ??
      "en");

export type LangContextData = {
  lang: I18n;
  setLocale: (locale: SupportedLanguages) => void;
  supportedLanguages: {
    langCode: string;
    countryCode: string;
    name: string;
  }[];
};

export const LangContext = React.createContext<LangContextData>(
  {} as LangContextData,
);

export const useLanguageContext = (): LangContextData => {
  const context = React.useContext(LangContext);
  if (!context) {
    throw new Error(
      "useLanguageContext must be used within a LanguageProvider",
    );
  }
  return context;
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [lang, setLang] = React.useState(
    new I18n(translations, { locale: resolvedLang, enableFallback: false }),
  );

  const setLocale = (locale: SupportedLanguages) => {
    localStorage.setItem("preferred-language", locale);
    setLang(new I18n(translations, { locale: locale, enableFallback: false }));
  };

  return (
    <LangContext.Provider value={{ lang, setLocale, supportedLanguages }}>
      {children}
    </LangContext.Provider>
  );
};
